import urlJoin from 'url-join';
import 'whatwg-fetch';
import { PublicClientApplication } from '@azure/msal-browser';
import { store } from '../store/configureStore';
import { graphConfig } from './../auth/authConfig';
import * as Constants from '../constants/other';

/**
 * Performs a fetch request with upload-specific guards:
 * - Pre-flight file size check for FormData uploads (rejects files exceeding 100 MB).
 * - Normalizes 413 (Payload Too Large) responses to include file names when possible.
 * - Maps connection reset errors during upload to synthetic 413 responses.
 *
 * @param {string} fullUrl - The full URL to fetch.
 * @param {RequestInit} [init] - The fetch options, including method, headers, and body.
 * @returns {Promise<Response>} Resolves to the fetch Response, or a synthetic 413 Response if size limits are exceeded or connection reset occurs.
 */
function fetchWithUploadGuards(fullUrl, init) {
  // Pre-flight size check.
  if (init && init.body instanceof FormData) {
    const oversizedNames = getOversizedFileNames(init.body);
    if (oversizedNames.length) {
      const namePart =
        oversizedNames.length === 1
          ? `file '${oversizedNames[0]}'`
          : `files ${oversizedNames.join(', ')}`;
      return Promise.resolve(
        synthesize413(`Upload failed: ${namePart} exceed(s) 100 MB limit.`)
      );
    }
  }
  const p = init ? fetch(fullUrl, init) : fetch(fullUrl);
  return p
    .then((r) => {
      if (r && r.status === 413) {
        // Attempt to surface file names from original request (may be empty if overhead triggered limit).
        const oversizedNames =
          init && init.body instanceof FormData
            ? getOversizedFileNames(init.body)
            : [];
        const namePart =
          oversizedNames.length === 0
            ? 'file'
            : oversizedNames.length === 1
              ? `file '${oversizedNames[0]}'`
              : `files ${oversizedNames.join(', ')}`;
        return synthesize413(
          `Upload failed: ${namePart} exceed(s) 100 MB limit.`
        );
      }
      return r;
    })
    .catch((err) => {
      const synthetic = tryMapConnectionResetTo413(err, init, fullUrl);
      if (synthetic) {
        return synthetic;
      }
      if (err instanceof TypeError && init && init.body instanceof FormData) {
        return synthesize413(
          'Upload failed: network connection was interrupted (possible size or network issue).'
        );
      }
      throw err;
    });
}

/**
 * Extracts the names of any File entries in a FormData that exceed the business limit.
 * @param {FormData} formData - The FormData object containing file entries.
 * @returns {string[]} Array of file names that exceed the size threshold.
 * Checks against the size threshold defined by Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES (100 MB).
 */
function getOversizedFileNames(formData) {
  if (!(formData instanceof FormData)) return [];
  const oversized = [];
  for (const [key, value] of formData.entries()) {
    if (
      value instanceof File &&
      typeof value.size === 'number' &&
      value.size > Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES
    ) {
      // Prefer the file's name; fall back to the field key if name missing.
      oversized.push(value.name || key);
    }
  }
  return oversized;
}

// Create a synthetic Response object with 413 status and JSON body.
/**
 * Creates a synthetic Response object with status 413 and a JSON body containing the provided message.
 * @param {string} message - The error message to include in the response body.
 * @returns {Response} A Response object with status 413 and a JSON body containing the message.
 */
function synthesize413(message) {
  return new Response(JSON.stringify({ message }), {
    status: 413,
    headers: { 'Content-Type': 'application/json' },
  });
}

// Attempt to map a connection reset error during upload into a synthetic 413 Response.
/**
 * Attempts to map a connection reset error during a FormData upload into a synthetic 413 Response.
 * Returns a synthetic 413 Response if the request body contains oversized files; otherwise returns null.
 *
 * @param {any} err - The error thrown during fetch (typically a network error).
 * @param {RequestInit} init - The fetch request options, expected to contain a FormData body.
 * @param {string} resource - The URL or resource being fetched.
 * @returns {Response|null} Synthetic 413 Response if oversized files are detected, otherwise null.
 * Returns null if the request is not a FormData upload or if no oversized files are present.
 */
function tryMapConnectionResetTo413(err, init, resource) {
  // Only attempt mapping for FormData uploads (file posts).
  if (
    !init ||
    !init.body ||
    typeof FormData === 'undefined' ||
    !(init.body instanceof FormData)
  ) {
    return null;
  }
  const oversizedNames = getOversizedFileNames(init.body);
  if (!oversizedNames.length) {
    return null; // Not a size-related issue we can confidently transform.
  }
  const namePart =
    oversizedNames.length === 1
      ? `file '${oversizedNames[0]}'`
      : `files ${oversizedNames.join(', ')}`;
  return synthesize413(
    `Upload failed: ${namePart} exceed(s) 100 MB limit (connection aborted before server responded).`
  );
}

/**
 * Middleware to handle if the response was Ok (2xx HTTP Code), or if we need to throw an Error on a non 2xx.
 * NOTE: This doesn't need to be used because of all the other places that manully look for an error response without a catch statement
 * @param {any} response The response object returned from fetch.
 * @returns {any} The response object that was passed in.
 */
const checkResponseOk = (response) => {
  if (response.ok) {
    return response;
  } else {
    throw Error(response.statusText);
  }
};

/**
 * Builds the headers sent with requests
 * @param {any} init The init object which may contain pre-existing headers.
 * @returns {any} An object containing headers.
 */
export function buildHeaders(init = null) {
  const token = store.getState().authentication.idToken;

  if (process.env.NODE_ENV !== 'test' && token) {
    const defaultHeaders = {
      Authorization: `Bearer ${token}`,
    };

    if (init !== undefined && init !== null) {
      let headers = init.headers;
      if (headers === undefined || headers === null) {
        init.headers = defaultHeaders;
      } else {
        headers.Authorization = `Bearer ${token}`;
        init.headers = headers;
      }

      return init;
    }

    return {
      headers: defaultHeaders,
      method: 'GET',
    };
  } else {
    if (init !== undefined && init !== null) {
      let headers = init.headers;
      if (headers === undefined || headers === null) {
        init.headers = new Headers();
      }

      return init;
    }
  }

  return init;
}

/**
 * Builds the headers to be sent with Microsoft Graph API requests
 * @param {any} init The init object which may contain pre-existing headers.
 * @returns {any} An object containing headers.
 */
export function buildHeadersForMicrosoftGraphApi(init = null) {
  const token = store.getState().authentication.accessToken;

  if (process.env.NODE_ENV !== 'test' && token) {
    const defaultHeaders = {
      Authorization: `Bearer ${token}`,
    };

    return {
      headers: defaultHeaders,
      method: 'GET',
    };
  } else {
    if (init !== undefined && init !== null) {
      let headers = init.headers;
      if (headers === undefined || headers === null) {
        init.headers = new Headers();
      }

      return init;
    }
  }

  return init;
}

/**
 * Fetch from the ClientAPI, which uses the root URL specified by the ROOT_CLIENT_API_URL environment variable.
 * In the dev environment, this variable is set in webpack.config.dev.js
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromClientApi(resource, init = null) {
  init = buildHeaders(init);
  const rootApiUrl = process.env.ROOT_CLIENT_API_URL;
  const fullUrl = urlJoin(rootApiUrl, resource);

  return init ? fetch(fullUrl, init) : fetch(fullUrl);
}

/**
 * Fetch from the ReitAPI, which uses the root URL specified by the ROOT_REIT_API_URL environment variable.
 * In the dev environment, this variable is set in webpack.config.dev.js
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromReitApi(resource, init = null) {
  init = buildHeaders(init);
  const rootApiUrl = process.env.ROOT_REIT_API_URL;
  const fullUrl = urlJoin(rootApiUrl, resource);
  return fetchWithUploadGuards(fullUrl, init);
}

/**
 * Fetch from the TrialBalanceAPI, which uses the root URL specified by the ROOT_TB_API_URL environment variable.
 * In the dev environment, this variable is set in webpack.config.dev.js
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromTrialBalanceApi(resource, init = null) {
  init = buildHeaders(init);
  const rootApiUrl = process.env.ROOT_TB_API_URL;
  const fullUrl = urlJoin(rootApiUrl, resource);
  return fetchWithUploadGuards(fullUrl, init);
}

/**
 * Fetch from the ReportAPI, which uses the root URL specified by the ROOT_REPORT_API_URL environment variable.
 * In the dev environment, this variable is set in webpack.config.dev.js
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromReportApi(resource, init = null) {
  init = buildHeaders(init);
  const rootApiUrl = process.env.ROOT_REPORT_API_URL;
  const fullUrl = urlJoin(rootApiUrl, resource);
  return fetchWithUploadGuards(fullUrl, init);
}

/**
 * Fetch from the AuthAPI, which uses the root URL specified by the ROOT_REPORT_API_URL environment variable.
 * In the dev environment, this variable is set in webpack.config.dev.js
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromAuthApi(resource, init = null) {
  init = buildHeaders(init);
  const rootApiUrl = process.env.ROOT_AUTH_API_URL;
  const fullUrl = urlJoin(rootApiUrl, resource);

  return init ? fetch(fullUrl, init) : fetch(fullUrl);
}

/**
 * Fetch from the microsoft Graph API, which uses the root URL specified by the graphConfig.graphApiV1Root constant in authConfig.js.
 * The root url is same for all the environments
 * @param {string} resource Path to the resource you wish to fetch
 * @param {Object} init An options object containing any custom settings that you want to apply to the request. See {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch} for more details.
 * @returns {Promise} A promise that resolves to the Response object representing the response to your request.
 */
export function fetchFromV1MicrosoftGraphApi(resource, init = null) {
  init = buildHeadersForMicrosoftGraphApi(init);
  const rootApiUrl = graphConfig.graphApiV1Root;
  const fullUrl = urlJoin(rootApiUrl, resource);

  return init ? fetch(fullUrl, init) : fetch(fullUrl);
}
